import { useQuery } from "@tanstack/react-query";

import api from "@/api/api";

const REFETCH_INTERVAL_MS = 60_000;
const TIMEOUT_MS = 5_000;

/**
 * Detects whether the API host answers at all.
 *
 * server/app.ts calls app.listen() inside the mongoose connect callback, so a dead database means
 * the server never starts listening and every request fails at the network layer. A /health
 * endpoint would therefore be unreachable during exactly the outage it was meant to report.
 *
 * The request uses no-cors on purpose: the response is opaque and unreadable, but it resolving at
 * all proves the host answered. That sidesteps any question of whether CORS headers survive an
 * error response, and a 404 counts as "up" — which is what we want.
 */
async function pingBackend(): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    await fetch(api.defaults.baseURL ?? "", { method: "GET", mode: "no-cors", cache: "no-store", signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export default function useBackendHealth(): boolean {
  const { data } = useQuery({
    queryKey: ["backend-health"],
    queryFn: pingBackend,
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: 30_000,
    retry: 1,
  });

  // Treat "not answered yet" as up, so the banner never flashes during a normal page load.
  return data ?? true;
}
